import { type ReactNode } from 'react';
import { Avatar } from '@/components/ui/avatar';
import { useAuth } from '@/hooks/use-auth';
import { pbFileUrl } from '@/utils/pb-file-url';
import { displayName } from '@/utils/formatters';
import { cn } from '@/utils/cn';
import { UserMenu } from './user-menu';
import { useUserMenu, type UserMenuState } from './use-user-menu';

function UserTrigger(p: {
  name: string;
  photoSrc?: string | undefined;
  collapsed: boolean;
  onClick: () => void;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
}): ReactNode {
  return (
    <button
      ref={p.triggerRef}
      onClick={p.onClick}
      className={cn(
        'flex items-center rounded-lg transition-colors hover:bg-surface-elevated',
        p.collapsed ? 'h-9 w-9 justify-center' : 'w-full gap-sm px-sm py-sm'
      )}
    >
      <Avatar src={p.photoSrc} name={p.name} size="sm" />
      {!p.collapsed && (
        <p className="shrink-0 whitespace-nowrap text-xs font-medium text-text-primary">
          {p.name}
        </p>
      )}
    </button>
  );
}

export function useUserDisplay(): { name: string; src?: string | undefined } {
  const { user } = useAuth();
  const name = user ? displayName(user) : '';
  const src = user?.photo ? pbFileUrl(user, user.photo) : undefined;
  return { name, src };
}

function BlockBody(p: {
  collapsed: boolean;
  name: string;
  src?: string | undefined;
  m: UserMenuState;
}): ReactNode {
  return (
    <>
      {p.m.rect && (
        <UserMenu
          anchor={p.m.rect}
          onClose={p.m.close}
          triggerRef={p.m.triggerRef}
        />
      )}
      <UserTrigger
        name={p.name}
        photoSrc={p.src}
        collapsed={p.collapsed}
        onClick={p.m.toggle}
        triggerRef={p.m.triggerRef}
      />
    </>
  );
}

export function SidebarUserBlock({
  collapsed,
}: {
  collapsed: boolean;
}): ReactNode {
  const { name, src } = useUserDisplay();
  const m = useUserMenu();
  return (
    <div
      className={cn(
        'space-y-1 border-t border-border-subtle pt-sm',
        collapsed && 'flex flex-col items-center'
      )}
    >
      <BlockBody collapsed={collapsed} name={name} src={src} m={m} />
    </div>
  );
}
